import type {
	ChangeEventHandler,
	FocusEventHandler,
	MouseEventHandler,
} from 'react'
import { cx } from 'utils/cx'

import type { OptionsType } from '../Select'

import styles from './MobileSelect.module.scss'
import texts from 'fukku/styles/texts.module.scss'

interface MobileSelectProps {
	readonly children?: React.ReactNode
	readonly disabled?: boolean
	readonly id: string
	readonly dataTestId?: string
	readonly name?: string
	readonly options: OptionsType[]
	readonly errorMessage?: string
	readonly showHint: boolean
	readonly selectedOption?: string | number
	readonly handleBlur?: FocusEventHandler<HTMLSelectElement>
	readonly handleChange?: ChangeEventHandler<HTMLSelectElement>
	readonly handleFocus?: FocusEventHandler<HTMLSelectElement>
	readonly handleHover?: MouseEventHandler<HTMLSelectElement>
	readonly placeholder?: string
}

export const MobileSelect = ({
	disabled = false,
	id,
	dataTestId,
	name = id,
	options,
	errorMessage,
	showHint,
	selectedOption,
	placeholder,
	handleChange = () => {
		/* does nothing */
	},
	handleFocus = () => {
		/* does nothing */
	},
	handleBlur = () => {
		/* does nothing */
	},
	handleHover = () => {
		/* does nothing */
	},
	children,
}: MobileSelectProps): React.ReactNode => {
	function getDescriberId() {
		if (errorMessage) {
			return `${id}-error`
		}
		if (showHint) {
			return `${id}-hint`
		}
		return undefined
	}

	return (
		<select
			id={id}
			name={name}
			data-testid={dataTestId}
			aria-describedby={getDescriberId()}
			aria-invalid={!!errorMessage || false}
			className={cx(
				styles.selector,
				children ? styles.selectorHidden : null,
				texts.bodyM
			)}
			aria-disabled={disabled}
			value={selectedOption ?? ''}
			onBlur={handleBlur}
			onChange={handleChange}
			onFocus={handleFocus}
			onMouseOver={handleHover}
		>
			<option hidden disabled />
			{placeholder && (
				<option disabled value={placeholder} label={placeholder} />
			)}
			{options?.map((option) => (
				<option
					key={`${option.value}${option.text ?? ''}`}
					className={styles.selector}
					value={option.value}
					disabled={option.disabled}
				>
					{option.text || option.value}
				</option>
			))}
		</select>
	)
}
